// HLS playback player (design spec §8A / §8B; frontend plan §4.5).
//
// The webapp had zero HLS before this (only fMP4 via <video>/MSE). hls.js is added
// as the ONLY new npm dep and is DYNAMICALLY IMPORTED so it code-splits out of the
// main bundle and only loads on a playback path. Safari (native HLS) plays the
// playlist URL directly; MSE browsers attach hls.js.
//
// CUSTOM LOADER SEAM: segments (and the playlist) can come from (a) a URL — cloud
// presign or device LAN http — or (b) the WebRTC data channel (webrtcPlayback.ts).
// The loader is injected via `loaderFactory`: when present, hls.js fetches every
// fragment/playlist through it instead of XHR, so the same player serves same-LAN
// http and remote data-channel tunnels.
//
// LIFECYCLE: the hls.js instance is destroyed on unmount AND on source change
// (c4 stop-on-move). A /seg 404 surfaces via onRecycle404 so View-2 can re-query
// day-ranges and rebuild (c5).

import { useEffect, useRef } from 'react';
import type Hls from 'hls.js';

/** A media source for the player. */
export type HlsSource =
  /** Existing cloud presign fMP4 — kept on the native <video> path (17 §3.1). */
  | { kind: 'cloud-clip'; url: string }
  /** Device VOD HLS playlist URL (same-LAN http, or a tunnel-relative URL + loader). */
  | { kind: 'device-vod'; m3u8Url: string };

/**
 * A custom loader factory: returns an hls.js Loader config class. Injected so the
 * caller (webrtcPlayback consumer) can route fragment/playlist loads through the
 * data channel. When omitted, hls.js uses its default XHR loader (same-LAN / cloud).
 */
export type HlsLoaderFactory = () => unknown;

export interface HlsPlayerProps {
  source: HlsSource;
  /** Invoked when a segment/playlist 404s (eviction → c5 refresh). */
  onRecycle404?: () => void;
  onEnded?: () => void;
  onError?: (fatal: boolean, detail: string) => void;
  /** Fires on the <video> timeupdate with the current play offset in seconds. */
  onTimeUpdate?: (currentTimeSec: number) => void;
  playbackRate?: number;
  /** Custom loader for data-channel tunneling (see webrtcPlayback.ts). */
  loaderFactory?: HlsLoaderFactory;
  className?: string;
  autoPlay?: boolean;
}

function sourceUrl(s: HlsSource): string {
  return s.kind === 'cloud-clip' ? s.url : s.m3u8Url;
}

export function HlsPlayer({
  source,
  onRecycle404,
  onEnded,
  onError,
  onTimeUpdate,
  playbackRate = 1,
  loaderFactory,
  className,
  autoPlay = true,
}: HlsPlayerProps) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const hlsRef = useRef<Hls | null>(null);
  // Keep the latest callbacks in refs so the attach effect depends only on the
  // source/loader (not on callers' inline closures), and reattaches on source change.
  const cbRef = useRef({ onRecycle404, onEnded, onError, onTimeUpdate });
  cbRef.current = { onRecycle404, onEnded, onError, onTimeUpdate };

  const url = sourceUrl(source);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    let cancelled = false;

    const teardown = () => {
      if (hlsRef.current) {
        try {
          hlsRef.current.destroy();
        } catch {
          /* already destroyed */
        }
        hlsRef.current = null;
      }
      // release native source too (c4 — drop the media connection).
      try {
        video.removeAttribute('src');
        video.load();
      } catch {
        /* jsdom / detached */
      }
    };

    // cloud-clip fMP4 and Safari native HLS both play the URL directly via <video>.
    const nativeHls = video.canPlayType('application/vnd.apple.mpegurl') !== '';
    if (source.kind === 'cloud-clip' || (source.kind === 'device-vod' && nativeHls && !loaderFactory)) {
      teardown();
      video.src = url;
      if (autoPlay) void video.play().catch(() => {});
      return teardown;
    }

    // MSE path: dynamic-import hls.js (code-split) and attach.
    void import('hls.js').then(({ default: HlsCtor }) => {
      if (cancelled || !videoRef.current) return;
      if (!HlsCtor.isSupported()) {
        if (loaderFactory) {
          cbRef.current.onError?.(true, 'hls.js unsupported for custom loader source');
          return;
        }
        // No MSE and not native: fall back to a direct src and let <video> try.
        video.src = url;
        if (autoPlay) void video.play().catch(() => {});
        return;
      }
      teardown();
      // Cloud event VOD is a concatenation of independent ~15s MPEG-TS clips joined
      // by EXT-X-DISCONTINUITY (each clip has its own PTS timeline). hls.js can
      // silently stall at a clip seam on a small timeline hole; widen the tolerated
      // hole and let it nudge across the seam instead of buffering forever.
      const config: Record<string, unknown> = {
        enableWorker: true,
        maxBufferHole: 1, // default 0.1s — skip a bigger gap at a clip seam
        highBufferWatchdogPeriod: 1, // watch for stalls more eagerly
        nudgeOffset: 0.2, // default 0.1s — bigger nudge per stall retry
        nudgeMaxRetry: 20, // default 3 — keep nudging past a stubborn seam
      };
      if (loaderFactory) config.loader = loaderFactory();
      const hls = new HlsCtor(config);
      hlsRef.current = hls;

      hls.on(HlsCtor.Events.ERROR, (_evt: unknown, data: { fatal?: boolean; type?: string; details?: string; response?: { code?: number } }) => {
        const code = data.response?.code;
        if (code === 404) cbRef.current.onRecycle404?.();
        cbRef.current.onError?.(Boolean(data.fatal), data.details ?? data.type ?? 'error');
        if (data.fatal) {
          // Let the caller decide recovery (View-2 rebuilds via day-ranges on c5).
          try {
            hls.destroy();
          } catch {
            /* noop */
          }
          if (hlsRef.current === hls) hlsRef.current = null;
        }
      });

      hls.loadSource(url);
      hls.attachMedia(video);
      hls.on(HlsCtor.Events.MANIFEST_PARSED, () => {
        if (autoPlay) void video.play().catch(() => {});
      });
    });

    const onVideoEnded = () => cbRef.current.onEnded?.();
    const onVideoTime = () => cbRef.current.onTimeUpdate?.(video.currentTime);
    video.addEventListener('ended', onVideoEnded);
    video.addEventListener('timeupdate', onVideoTime);

    return () => {
      cancelled = true;
      video.removeEventListener('ended', onVideoEnded);
      video.removeEventListener('timeupdate', onVideoTime);
      teardown();
    };
    // Reattach on source URL or loader change (c4 stop-on-move). Callbacks live in refs.
  }, [url, source.kind, loaderFactory, autoPlay]);

  // Apply playback rate without reattaching the source.
  useEffect(() => {
    const video = videoRef.current;
    if (video) video.playbackRate = playbackRate;
  }, [playbackRate, url]);

  return <video ref={videoRef} className={className} controls playsInline style={{ width: '100%', background: '#000' }} />;
}
